// §7.7 — host-OS import.
//
// A `drop` from the real desktop carries a `DataTransferItemList`. Plain files come out of
// `getAsFile()`; a *directory* only exists through `webkitGetAsEntry()`, so the walk is over
// entries where they are available and over files where they are not — which is also what makes
// a synthetic drop (a test, or a paste) work at all, since those have no entries.
//
// Nothing here decides *where* the bytes go: the caller supplies a confined writer, so an import
// is subject to §9.5 exactly like every other write.
import { joinVfs, uniqueName, type VfsPath } from '@hangar/shared';

export interface ImportSink {
  write(path: VfsPath, bytes: Uint8Array): Promise<unknown>;
  mkdir(path: VfsPath): Promise<unknown>;
  /** Names already in `dir`, so an import never overwrites (D-045). */
  taken(dir: VfsPath): Promise<string[]>;
}

export interface ImportResult {
  /** Files actually written. */
  files: number;
  bytes: number;
  /** Items that could not be read. An unreadable one never abandons the rest. */
  failed: number;
  /** Where the top-level items landed, for the caller to select. */
  landed: VfsPath[];
}

/** The part of `DataTransfer` this reads. Narrow, so a test can hand it a plain object. */
export interface TransferLike {
  items?: ArrayLike<DataTransferItemLike> | undefined;
  files?: ArrayLike<File> | undefined;
}

export interface DataTransferItemLike {
  kind: string;
  getAsFile(): File | null;
  webkitGetAsEntry?: () => FileSystemEntryLike | null;
}

export interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (ok: (file: File) => void, fail: (error: unknown) => void) => void;
  createReader?: () => {
    readEntries: (ok: (entries: FileSystemEntryLike[]) => void, fail: (e: unknown) => void) => void;
  };
}

function fileOf(entry: FileSystemEntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    if (!entry.file) {
      reject(new Error(`import: ${entry.name} cannot be read`));
      return;
    }
    entry.file(resolve, reject);
  });
}

/** `readEntries` returns a *batch*; the directory is only finished when a batch comes back empty. */
async function childrenOf(entry: FileSystemEntryLike): Promise<FileSystemEntryLike[]> {
  const reader = entry.createReader?.();
  if (!reader) return [];
  const all: FileSystemEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntryLike[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) return all;
    all.push(...batch);
  }
}

async function bytesOf(file: File): Promise<Uint8Array> {
  return new Uint8Array(await file.arrayBuffer());
}

export async function importTransfer(
  transfer: TransferLike,
  dir: VfsPath,
  sink: ImportSink,
): Promise<ImportResult> {
  const result: ImportResult = { files: 0, bytes: 0, failed: 0, landed: [] };
  const taken = new Set(await sink.taken(dir));

  const writeFile = async (file: File, into: VfsPath, name: string): Promise<VfsPath> => {
    const bytes = await bytesOf(file);
    const target = joinVfs(into, name);
    await sink.write(target, bytes);
    result.files += 1;
    result.bytes += bytes.length;
    return target;
  };

  const walk = async (entry: FileSystemEntryLike, into: VfsPath, name: string): Promise<VfsPath> => {
    if (entry.isFile) return writeFile(await fileOf(entry), into, name);
    const target = joinVfs(into, name);
    await sink.mkdir(target);
    // Inside a freshly created directory nothing is taken, so children keep their own names.
    const used = new Set<string>();
    for (const child of await childrenOf(entry)) {
      const childName = uniqueName(used, child.name);
      used.add(childName);
      try {
        await walk(child, target, childName);
      } catch {
        result.failed += 1;
      }
    }
    return target;
  };

  const items = transfer.items ? Array.from(transfer.items) : [];
  const dropped = items.filter((item) => item.kind === 'file');

  // A drop with no item list at all — some browsers, and every synthetic `DataTransfer` built
  // from `files` alone — still has `files`.
  const plain = dropped.length === 0 && transfer.files ? Array.from(transfer.files) : [];

  for (const item of dropped) {
    const entry = item.webkitGetAsEntry?.() ?? null;
    const file = entry ? null : item.getAsFile();
    const rawName = entry?.name ?? file?.name;
    if (rawName === undefined || (entry === null && file === null)) {
      result.failed += 1;
      continue;
    }
    const name = uniqueName(taken, rawName);
    taken.add(name);
    try {
      result.landed.push(entry ? await walk(entry, dir, name) : await writeFile(file as File, dir, name));
    } catch {
      result.failed += 1;
      taken.delete(name);
    }
  }

  for (const file of plain) {
    const name = uniqueName(taken, file.name);
    taken.add(name);
    try {
      result.landed.push(await writeFile(file, dir, name));
    } catch {
      result.failed += 1;
      taken.delete(name);
    }
  }

  return result;
}
